import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  GENDER_OPTIONS,
  MAX_OF_ACCOUNTS_PER_PLAYER,
  RECORD_SOURCE,
  USER_TYPE,
} from 'src/app/core/helpers/global/global.constant';
import {
  PLAYER_DEFAULT_STATE,
  PLAYER_REGISTER_VALIDATIONS,
} from 'src/app/core/helpers/global/player.constant';
import { TYPES_USER_PERMISSIONS } from 'src/app/core/helpers/global/user-management.constant';
import { NG_SELECT_QUERIES } from 'src/app/core/helpers/ui/ui.constant';
import { Player } from 'src/app/core/interfaces/api/player.interface';
import { User } from 'src/app/core/interfaces/api/user.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { NgSelect, NgSelectQuery } from 'src/app/core/interfaces/ui/ui.interface';
import { CityService } from 'src/app/core/services/api/city.service';
import { CountryService } from 'src/app/core/services/api/country.service';
import { CurrencyService } from 'src/app/core/services/api/currency.service';
import { LanguageService } from 'src/app/core/services/api/language.service';
import { PlayerService } from 'src/app/core/services/api/player.service';
import { UserService } from 'src/app/core/services/api/user.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { FilterService } from 'src/app/core/services/ui/filter.service';
import { SearchNgSelectService } from 'src/app/core/services/ui/search-ng-select.service';
import { AppState } from 'src/app/core/states/app.state';
import { selectUser } from 'src/app/core/states/auth/auth.selectors';
import { adultValidator } from 'src/app/shared/validators/validatorsAdult';
import { phoneNumberValidator } from 'src/app/shared/validators/validatorsPhoneNumber';

@Component({
  selector: 'player-modal-form',
  templateUrl: './player-modal-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: 'ngCountries', useClass: SearchNgSelectService },
    { provide: 'ngCities', useClass: SearchNgSelectService },
    { provide: 'ngCurrencies', useClass: SearchNgSelectService },
    { provide: 'ngLanguages', useClass: SearchNgSelectService },
    { provide: 'ngUsers', useClass: SearchNgSelectService },
    { provide: 'ngCountriesWithPhoneCodes', useClass: SearchNgSelectService },
  ],
})
export class PlayerModalFormComponent implements OnInit {
  public USER_TYPE = TYPES_USER_PERMISSIONS;
  public NG_SELECT_QUERIES = NG_SELECT_QUERIES;
  public PLAYER_REGISTER_VALIDATIONS = PLAYER_REGISTER_VALIDATIONS;

  public countries$: Observable<NgSelect<string>[]> = of([]);
  public cities$: Observable<NgSelect<string>[]> = of([]);
  public currencies$: Observable<NgSelect<string>[]> = of([]);
  public languages$: Observable<NgSelect<string>[]> = of([]);
  public genders$: Observable<NgSelect<string>[]> = of(GENDER_OPTIONS);
  public users$: Observable<NgSelect<string>[]> = of([]);
  public countriesWithPhoneCodes$: Observable<NgSelect<string>[]> = of([]);

  public playerForm: FormGroup | undefined = undefined;

  public showPassword = signal(false);

  public readonly profile = toSignal(this._store.select(selectUser));

  constructor(
    @Inject('ngUsers')
    private readonly _ngUsers: SearchNgSelectService<string>,
    private readonly _userService: UserService,
    @Inject('ngCountries')
    private readonly _ngCountries: SearchNgSelectService<string>,
    private readonly _countryService: CountryService,
    @Inject('ngCities')
    private readonly _ngCities: SearchNgSelectService<string>,
    private readonly _cityService: CityService,
    @Inject('ngCurrencies')
    private readonly _ngCurrencies: SearchNgSelectService<string>,
    private readonly _currencyService: CurrencyService,
    @Inject('ngLanguages')
    private readonly _ngLanguages: SearchNgSelectService<string>,
    private readonly _languageService: LanguageService,
    @Inject('ngCountriesWithPhoneCodes')
    private readonly _ngCountriesWithPhoneCodes: SearchNgSelectService<string>,

    private readonly _bsModalService: BootstrapModalService<ModalWithAction<Player>>,
    private readonly _filterService: FilterService<object>,
    private readonly _formBuilder: FormBuilder,
    private readonly _playerService: PlayerService,
    private readonly _store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.setConfigNgSelect();
    this.buildPlayerForm();
  }

  private setConfigNgSelect(): void {
    this._ngCurrencies.setSearchTermKey('name');
    this._ngCurrencies.setFetchDataFunction(
      this._currencyService.findCurrenciesForSelect.bind(this._currencyService)
    );
    this.currencies$ = this._ngCurrencies.getData();
    this._ngCurrencies.triggerFetchData();

    this._ngCountries.setSearchTermKey('name');
    this._ngCountries.setFetchDataFunction(
      this._countryService.findCountriesForSelect.bind(this._countryService)
    );
    this.countries$ = this._ngCountries.getData();
    this._ngCountries.triggerFetchData();

    this._ngCities.setSearchTermKey('name');
    this._ngCities.setFetchDataFunction(
      this._cityService.findCitiesForSelect.bind(this._cityService)
    );
    this.cities$ = this._ngCities.getData();

    this._ngCountriesWithPhoneCodes.setSearchTermKey('searchTerm');
    this._ngCountriesWithPhoneCodes.setFetchDataFunction(
      this._countryService.findTelephoneCodesForSelect.bind(this._countryService)
    );
    this.countriesWithPhoneCodes$ = this._ngCountriesWithPhoneCodes.getData();
    this._ngCountriesWithPhoneCodes.triggerFetchData();

    this._ngLanguages.setSearchTermKey('name');
    this._ngLanguages.setFetchDataFunction(
      this._languageService.findLanguagesForSelect.bind(this._languageService)
    );
    this.languages$ = this._ngLanguages.getData();
    this._ngLanguages.triggerFetchData();

    this._ngUsers.setSearchTermKey('username');
    this._ngUsers.extendFilter({ type: USER_TYPE.AGENT });
    this._ngUsers.setFetchDataFunction(
      this._userService.findUsersForSelect.bind(this._userService)
    );
    this.users$ = this._ngUsers.getData();
    this._ngUsers.triggerFetchData();
  }

  private buildPlayerForm(): void {
    this.playerForm = this._formBuilder.group({
      username: ['', Validators.required],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(PLAYER_REGISTER_VALIDATIONS.PASSWORD.MIN_LENGTH),
          Validators.maxLength(PLAYER_REGISTER_VALIDATIONS.PASSWORD.MAX_LENGTH),
        ],
      ],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      birthDate: ['', [Validators.required, adultValidator()]],
      gender: [null, Validators.required],
      countryId: [null, Validators.required],
      cityId: [null, Validators.required],
      address: [''],
      languageId: [null, Validators.required],
      zipCode: [''],
      mobileNumber: [null, [Validators.required, phoneNumberValidator()]],
      telephoneCodeId: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      balance: [''],
      currencyId: [this.resolveInitialCurrencyId()],
      loyaltyProgram: [false],
      callSubscription: [true],
      pushSubscription: [true],
      messageSubscription: [true],
      mailSubscription: [true],
      state: [PLAYER_DEFAULT_STATE],
      agentId: [this.resolveInitialAgentId(), Validators.required],
      recordSource: [RECORD_SOURCE.BACK_OFFICE],
    });
  }

  private resolveInitialCurrencyId(): string | null {
    if (this.isAgentOrCashdesk || this.isCollaboratorAgent) {
      return this.profile()?.currencyId?._id ?? null;
    }
    return null;
  }

  private resolveInitialAgentId(): string | null {
    if (this.isAgentOrCashdesk) return this.profile()?._id ?? null;
    if (this.isCollaboratorAgent) return this.profile()?.parentId?._id ?? null;
    return null;
  }

  get isCollaboratorAgent(): boolean {
    return this.profile()?.userType === TYPES_USER_PERMISSIONS.COLLABORATOR_AGENT;
  }

  get isAgentOrCashdesk(): boolean {
    return (
      this.profile()?.userType === TYPES_USER_PERMISSIONS.AGENT ||
      this.profile()?.userType === TYPES_USER_PERMISSIONS.CASHDESK
    );
  }

  get canSelectAgent(): boolean {
    return !this.isAgentOrCashdesk && !this.isCollaboratorAgent;
  }

  public onSearchSelect(typeQuery: NgSelectQuery, searchTerm?: string): void {
    const term = searchTerm || '';
    const actionMap = new Map<NgSelectQuery, () => void>([
      [NG_SELECT_QUERIES.COUNTRIES, () => this._ngCountries.searchTerm(term)],
      [NG_SELECT_QUERIES.CITIES, () => this.onSearchTermForCities(term)],
      [NG_SELECT_QUERIES.CURRENCIES, () => this._ngCurrencies.searchTerm(term)],
      [NG_SELECT_QUERIES.LANGUAGE, () => this._ngLanguages.searchTerm(term)],
      [NG_SELECT_QUERIES.USERS, () => this._ngUsers.searchTerm(term)],
      [NG_SELECT_QUERIES.TELEPHONE_CODES, () => this._ngCountriesWithPhoneCodes.searchTerm(term)],
    ]);

    const action = actionMap.get(typeQuery);
    if (action) action();
  }

  public onScrollToEndSelect(typeQuery: NgSelectQuery): void {
    const actionMap = new Map<NgSelectQuery, () => void>([
      [NG_SELECT_QUERIES.COUNTRIES, () => this._ngCountries.scrollToEnd()],
      [NG_SELECT_QUERIES.CITIES, () => this._ngCities.scrollToEnd()],
      [NG_SELECT_QUERIES.CURRENCIES, () => this._ngCurrencies.scrollToEnd()],
      [NG_SELECT_QUERIES.LANGUAGE, () => this._ngLanguages.scrollToEnd()],
      [NG_SELECT_QUERIES.USERS, () => this._ngUsers.scrollToEnd()],
      [NG_SELECT_QUERIES.TELEPHONE_CODES, () => this._ngCountriesWithPhoneCodes.scrollToEnd()],
    ]);

    const action = actionMap.get(typeQuery);
    if (action) action();
  }

  public onCountryChange(event?: NgSelect<string>): void {
    this._ngCities.resetNgSelect();
    this.playerForm?.patchValue({ cityId: null });

    if (!event) return;

    this._ngCities.extendFilter({ countryId: event.value });
    this._ngCities.triggerFetchData();
  }

  public onUserChange(selectedAgent: User): void {
    const currencyId = selectedAgent?.currencies?.[0]?.currencyId?._id ?? null;
    this.playerForm?.patchValue({ currencyId });
  }

  public onSubmit(): void {
    if (!this.playerForm) return;

    if (this.playerForm.invalid) {
      this.playerForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPlayerPayload();
    console.log('🚀 ~ PlayerModalFormComponent ~ onSubmit ~ payload:', payload);

    this._playerService.createPlayer(payload).subscribe({
      next: () => {
        this._filterService.updateFilterData({});
        this.closeModal();
      },
    });
  }

  public togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  public closeModal(): void {
    this._bsModalService.closeModal();
  }

  private onSearchTermForCities(term: string): void {
    if (!this.playerForm) return;
    const { countryId } = this.playerForm.value;
    this._ngCities.extendFilter({ countryId });
    this._ngCities.searchTerm(term);
  }

  private buildPlayerPayload(): object {
    return {
      ...this.playerForm!.value,
      maxAccountsPlayer: MAX_OF_ACCOUNTS_PER_PLAYER,
      ...(this.isAgentOrCashdesk && {
        agentId: this.profile()?._id,
        currencyId: this.profile()?.currencyId?._id,
      }),
    };
  }
}
